// ----------------------------------------------------------------------------
// Spring Calculator: Engine
// ----------------------------------------------------------------------------
//
// Pure calculation module. Ported from FOX's tech-site spring calculator
// (calculator.js). No DOM access, so it can be unit-tested in isolation.
//
// Inputs are metric except rider weight, which is taken in lbs. Output is a
// spring rate in lbs/in selected from the catalogue of rates FOX produces for
// the given stroke.
//
// ----------------------------------------------------------------------------

export const LBS_PER_KG = 2.20462;
const MM_PER_INCH = 25.4;

// Weight of the bike itself (lbs), keyed by the nearest reference stroke.
const BIKE_WEIGHT_LBS = {
    pedal: { 45: 29, 55: 32, 65: 36, 75: 41 },
    ebike: { 45: 48, 55: 54, 65: 56, 75: 58 },
};

// Target rear-wheel sag as a fraction of travel, keyed by the nearest reference stroke.
const BASE_SAG = {
    suggested: { 45: 0.28, 55: 0.30, 65: 0.31, 75: 0.31 },
    pro:       { 45: 0.27, 55: 0.28, 65: 0.285, 75: 0.285 },
};

// Riding-style slider (0 = soft, 100 = stiff) shifts the target sag linearly.
const SAG_ADJUST_AT_SOFT = 0.018;
const SAG_ADJUST_RANGE = 0.030;

// Percentage of rider / bike weight carried by the rear wheel.
const RIDER_REAR_BIAS = 0.57;
const BIKE_REAR_BIAS = 0.518;

// Reference strokes used by the lookup tables above.
const REFERENCE_STROKES = [45, 55, 65, 75];

// Number of points used when integrating the leverage curve.
const CURVE_POINTS = 100;

/**
 * Spring rates (lbs/in) FOX offers for a given stroke range.
 */
export const getSpringRateOptions = (strokeMm) => {
    if (strokeMm <= 45) {
        return [250, 300, 325, 350, 550, 575, 600, 650, 700, 750];
    }
    if (strokeMm <= 55) {
        return [250, 275, 300, 325, 350, 375, 400, 425, 450, 475, 500, 550, 600, 650, 700, 750];
    }
    if (strokeMm <= 65) {
        return [225, 250, 275, 300, 325, 350, 375, 400, 425, 450, 475, 500, 550, 600, 650, 700, 800];
    }
    return [150, 225, 250, 275, 300, 325, 350, 375, 400, 425, 450, 475, 500, 525, 550, 575, 600, 650, 700, 800];
};

/**
 * Assumed leverage-curve progression (%) for a stroke when the frame is unknown.
 */
export const getProgression = (strokeMm) => {
    if (strokeMm <= 50) return 17;
    if (strokeMm <= 60) return 23;
    if (strokeMm <= 70) return 26;
    return 33;
};

const nearestReferenceStroke = (strokeMm) => REFERENCE_STROKES.reduce((prev, curr) => (
    Math.abs(curr - strokeMm) < Math.abs(prev - strokeMm) ? curr : prev
));

export const getBikeWeightLbs = (strokeMm, isEbike) => {
    const table = isEbike ? BIKE_WEIGHT_LBS.ebike : BIKE_WEIGHT_LBS.pedal;
    return table[nearestReferenceStroke(strokeMm)];
};

/**
 * Target rear-wheel sag (fraction of travel) for the stroke, rider level and riding style.
 */
export const getTargetSag = (strokeMm, ridingStyle, isPro) => {
    const table = isPro ? BASE_SAG.pro : BASE_SAG.suggested;
    const baseSag = table[nearestReferenceStroke(strokeMm)];
    const adjustment = SAG_ADJUST_AT_SOFT - (ridingStyle / 100) * SAG_ADJUST_RANGE;
    return baseSag + adjustment;
};

/**
 * Linear interpolation of y at xi over sorted x. Extrapolates past the ends.
 */
export const interp1 = (x, y, xi) => {
    let i = 0;
    while (i < x.length && x[i] < xi) i++;

    if (i < x.length && x[i] === xi) return y[i];
    if (i === 0) i = 1;
    else if (i === x.length) i = x.length - 1;

    return y[i - 1] + (xi - x[i - 1]) * (y[i] - y[i - 1]) / (x[i] - x[i - 1]);
};

/**
 * Builds the wheel-force curve for one spring rate.
 *
 * The leverage ratio is assumed to fall linearly from a start ratio to an end
 * ratio across the travel. Shock displacement is integrated from the wheel
 * displacement through the leverage ratio, then spring force is converted
 * back to force at the wheel.
 *
 * @returns {{ wheelTravelIn: number[], forceAtWheel: number[] }}
 */
const buildWheelForceCurve = ({ springRate, travelIn, startRatio, avgRatio, endRatio }) => {
    const anchorsTravel = [0, travelIn / 2, travelIn];
    const anchorsRatio = [startRatio, avgRatio, endRatio];

    const step = travelIn / (CURVE_POINTS - 1);
    const wheelTravelIn = [];
    const leverage = [];
    const shockStroke = [];
    const forceAtWheel = [];

    for (let i = 0; i < CURVE_POINTS; i++) {
        const travel = i * step;
        const ratio = interp1(anchorsTravel, anchorsRatio, travel);
        wheelTravelIn.push(travel);
        leverage.push(ratio);

        if (i === 0) {
            shockStroke.push(0);
        } else {
            const avgLeverage = (ratio + leverage[i - 1]) / 2;
            shockStroke.push(shockStroke[i - 1] + (travel - wheelTravelIn[i - 1]) / avgLeverage);
        }

        forceAtWheel.push((springRate * shockStroke[i]) / ratio);
    }

    return { wheelTravelIn, forceAtWheel };
};

/**
 * Recommend a spring rate.
 *
 * @param {Object} input
 * @param {number} input.riderWeightLbs   Rider weight, geared up, in lbs
 * @param {number} input.rearWheelTravelMm Rear wheel travel in mm
 * @param {number} input.strokeMm          Shock stroke in mm
 * @param {boolean} input.isEbike
 * @param {boolean} input.isPro            Pro sag targets instead of suggested
 * @param {number} input.ridingStyle       0 (soft) to 100 (stiff)
 *
 * @returns {{ rate: number, targetSagPercent: number, actualSagPercent: number, candidates: number[] }}
 */
export const calculateSpringRate = ({
    riderWeightLbs,
    rearWheelTravelMm,
    strokeMm,
    isEbike = false,
    isPro = false,
    ridingStyle = 50,
}) => {
    const bikeWeightLbs = getBikeWeightLbs(strokeMm, isEbike);
    const progression = getProgression(strokeMm) / 100;
    const targetSag = getTargetSag(strokeMm, ridingStyle, isPro);

    const strokeIn = strokeMm / MM_PER_INCH;
    const travelIn = rearWheelTravelMm / MM_PER_INCH;
    const avgRatio = travelIn / strokeIn;
    const startRatio = (-2 * avgRatio) / (progression - 2);
    const endRatio = (2 * avgRatio) - startRatio;

    const targetSagIn = targetSag * travelIn;
    const weightAtRear = riderWeightLbs * RIDER_REAR_BIAS + bikeWeightLbs * BIKE_REAR_BIAS;

    const candidates = getSpringRateOptions(strokeMm);
    let best = { rate: null, error: Infinity, curve: null };

    candidates.forEach((springRate) => {
        const curve = buildWheelForceCurve({ springRate, travelIn, startRatio, avgRatio, endRatio });
        const forceAtTargetSag = interp1(curve.wheelTravelIn, curve.forceAtWheel, targetSagIn);
        const error = Math.abs(forceAtTargetSag - weightAtRear);

        if (error < best.error) {
            best = { rate: springRate, error, curve };
        }
    });

    // Where on the curve does the chosen spring actually settle under the rider?
    let sagIndex = 0;
    let minDiff = Infinity;
    best.curve.forceAtWheel.forEach((force, index) => {
        const diff = Math.abs(force - weightAtRear);
        if (diff < minDiff) {
            minDiff = diff;
            sagIndex = index;
        }
    });

    return {
        rate: best.rate,
        targetSagPercent: Math.round(targetSag * 1000) / 10,
        actualSagPercent: Math.round((best.curve.wheelTravelIn[sagIndex] / travelIn) * 1000) / 10,
        candidates,
    };
};

/**
 * Short description of the chosen riding style, shown alongside the result.
 */
export const describeRidingStyle = (ridingStyle, isPro) => {
    if (isPro) {
        if (ridingStyle <= 40) return 'Firm suspension for aggressive riding.';
        if (ridingStyle <= 70) return 'We\'re not saying no, but 98% of riders will find this too stiff.';
        return 'Recommended if you\'ve been on the hot seat.';
    }

    if (ridingStyle <= 20) return 'Sticks like Velcro. Max traction and comfort, but compromises support and bottom-out force.';
    if (ridingStyle <= 70) return 'We recommend riders start here. A good balance of support and traction.';
    return 'Firm suspension for aggressive riding.';
};
